<template>
  <div class="setting-content--section">
    <TitleBar>
      <template v-slot:title>
        <h1>{{ $gettext('My subscription') }}</h1>
      </template>
    </TitleBar>
    <div class="content">
      <div style="width: 100%">
        <div v-if="!loading && !activePlan" class="subscriptions-wrap">
          <div class="text-center" style="font-size: 1rem;" v-html="introText" />
          <div class="plan-cycle-wrap">
            <span v-if="isActive" class="billingcycle-date">
              {{ $gettext('Your current subscription is valid until') }} {{ activePlan.end_date | formatDate }}
            </span>
            <span v-else-if="!isFree" class="billingcycle-date"> {{ $gettext('End date') }} {{ endBillingDate }} </span>
            <div class="plan-cycle-switch-wrap">
              <span @click="yearlyCycle = false" class="left-selector">{{ $gettext('Monthly') }}</span>
              <b-form-checkbox v-model="yearlyCycle" switch size="lg">
                <span class="right-selector">
                  {{ $gettext('Yearly') }} <span class="yearly-offer">{{ discountText }}</span>
                </span>
              </b-form-checkbox>
            </div>
          </div>

          <div class="subscriptions-tiers-wrap">
            <!-- all tiers (also free/starter) -->
            <TierPlanBox
              v-for="(tierOption, index) in getAvailableTiers"
              :key="index"
              :tier-data="tierOption"
              :yearly-cycle="yearlyCycle"
              :cycle-multiplier="selectedCycleMultiplier"
              :active-discount="activeDiscount"
              @create-plan="createPlan"
              @update-subscription="updateSubscription"
            />
          </div>
        </div>
        <div v-if="loading && !activePlan" class="subscriptions-wrap">
          <div style="height: 125px;">

          </div>
          <div class="subscriptions-tiers-wrap">
            <SkeletonPlanBox />
            <SkeletonPlanBox />
            <SkeletonPlanBox />
            <SkeletonPlanBox />
          </div>
        </div>
        <div v-if="loading && activePlan" class="subscriptions-wrap subscriptions-wrap--activeplan" >
          <div class="subscriptions-tiers-wrap">
            <SkeletonPlanBox active-plan />
          </div>
        </div>
        <div
          v-if="!loading && activePlan"
          class="subscriptions-wrap subscriptions-wrap--activeplan"
        >
          <div class="subscriptions-activeplan">
            <ActivePlanBox
              :tier-data="currentTier"
              :yearly-cycle="yearlyCycle"
              :cycle-multiplier="selectedCycleMultiplier"
              :active-discount="activeDiscount"
            />
            <div class="activeplan-wrap">
              <h3>{{ $gettext('Subscription & billing info') }}</h3>
              <div v-if="isActive">

                <div v-if="!isCancelled">
                  <b-alert v-if="isGrace" show class="gk-alert mt-4" variant="warning">
                    <div v-if="!isFree" class="subscription-payment-alert">
                      <p class="mb-0" v-translate="{ grace_ends: $options.filters.formatDate(activePlan.grace_date) }">
                        Your payment was not successful. You will therefore be downgraded on %{grace_ends} unless you
                        complete the payment before that time.
                      </p>
                      <GekkoButton
                        @click="repayPlan()"
                        icon="fa-regular fa-credit-card"
                        :text="$gettext('Retry payment')"
                        class="mt-2 position-relative"
                        :loading="isWaitingForResponse"
                      />
                    </div>
                    
                    <div v-if="isFree">
                      <p class="mb-0" v-translate="{ grace_ends: $options.filters.formatDate(activePlan.grace_date) }">
                        You have been assigned the {{ currentTier.name }} plan. Upgrade to a paid plan before {{ activePlan.grace_date | formatDate }} to avoid being downgraded. If you are downgraded, all your existing connections will be deactivated, and you will be limited to 3 invitations.
                      </p>
                      <GekkoButton
                        @click="repayPlan()"
                        icon="fa-regular fa-credit-card"
                        :text="$gettext('Upgrade to paid plan')"
                        class="mt-2 position-relative"
                        :loading="isWaitingForResponse"
                      />
                    </div>
                  </b-alert>

                  <b-alert v-if="!isGrace" show class="gk-alert next-payment-alert" variant="info">
                    <div v-if="nextPayment" v-translate="{ next_payment: beautifiedNextPayment }">
                      Your next payment for <strong :key="nextPlanName">{{ nextPlanName }}</strong> of
                      <span class="text-brand">€%{next_payment}</span> occurs on:<br />
                      <strong>
                        {{ activePlan.end_date | formatDate }}
                        <font-awesome-icon
                          id="tooltip-subscriptions"
                          class="tooltip-icon tooltip-icon--question ml-2"
                          icon="fa-regular fa-circle-question"
                        />
                        <b-tooltip
                          :custom-class="'tooltip-padded'"
                          target="tooltip-subscriptions"
                          triggers="hover"
                          container="#report-container"
                        >
                          <div class="tooltip-title">
                            {{ $gettext('Subscriptions') }}
                          </div>
                          <div class="tooltip-text">
                            {{
                              $gettext(
                                'Gekko Premium is a subscription for which automatic recurring payments are required. You can cancel your subscription at any time before this date at no expense and your Premium subscription will automatically stop.'
                              )
                            }}
                          </div>
                        </b-tooltip>
                      </strong>
                      <div
                        v-if="currentCycle === 12"
                        v-translate="{ total_discount_next: totalDiscountNext }"
                        :key="totalDiscountNext"
                      >
                        You save <span class="text-brand">€%{total_discount_next}</span> by billing annually.
                      </div>
                      <div>
                        <span style="display: block; font-size: 14px; opacity: 0.6"
                          >({{ $gettext('VAT applies') }})</span
                        >
                      </div>
                    </div>
                    <div v-if="!nextPayment">
                      {{ $gettext('You will not be charged in the next billing cycle for your current number of connections.') }}
                    </div>
                  </b-alert>
                </div>

                <div v-if="isCancelled">
                  <b-alert show class="gk-alert mt-4" variant="danger">
                    <div class="subscription-payment-alert">
                      <p class="mb-0">
                        {{
                          $gettext('You have cancelled your subscription and your customers will be disconnected on:')
                        }}
                        &nbsp;
                        <strong>{{ isCancelled | formatDate }}</strong>
                      </p>
                      <GekkoButton
                        @click="updateSubscription(currentTier.id)"
                        :text="$gettext('Reactivate subscription')"
                        icon="fa-solid fa-arrow-right"
                        icon-right
                      />
                    </div>
                  </b-alert>
                </div>

                <div v-if="!isCancelled && !isGrace">
                  <p>
                    <GekkoButton
                      :text="$gettext('Click here if you want to cancel your subscription')"
                      v-gk-confirm="{
                        title: $gettext('Downgrade to Starter (Free) plan'),
                        message: $gettext(
                          'Are you sure you want to downgrade to the Free plan? Your existing customer connections will be disconnected at the end of your current billing cycle, and you will lose access to your customers\' companies.'
                        ),
                        options: {
                          variant: 'danger',
                          confirmText: $gettext('Downgrade'),
                        },
                        callback: updateSubscription,
                      }"
                      class="position-relative p-0 text-blue"
                      variant="link"
                      size="sm"
                      :loading="isWaitingForResponse"
                    />
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-if="billingSettings">
          <BillingInvoices />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex'
import settingsPageSetup from '@/mixins/settingsPageSetup'
import BillingInvoices from '@/components/invoices/BillingInvoices'
import PlanBoxPremium from '@/components/subscriptions/PlanBoxPremium'
import TierPlanBox from '@/components/subscriptions/TierPlanBox'
import ActivePlanBox from '@/components/subscriptions/ActivePlanBox'
import SkeletonPlanBox from '@/components/subscriptions/SkeletonPlanBox'
import PlanService from '@/store/services/plan.service'
import GekkoButton from '@/components/GekkoButton'
import TitleBar from '@/components/TitleBar'
import moment from 'moment'

export default {
  name: 'AccountantSubscriptions',
  components: {
    PlanBoxPremium,
    GekkoButton,
    BillingInvoices,
    TitleBar,
    TierPlanBox,
    ActivePlanBox,
    SkeletonPlanBox
  },
  mixins: [settingsPageSetup],
  data() {
    return {
      pageSettings: {
        title: this.$gettext('Accountant Subscriptions'),
        showCompanyPicker: false,
      },
      yearlyCycle: false,
      loading: true
    }
  },
  created() {
    this.setupData()
  },
  computed: {
    ...mapGetters({
      isPremium: 'getIsPremium', // not set anywhere in accountants
      activeDiscount: 'subscriptionStore/getDiscount',
      activePlan: 'subscriptionStore/getActivePlan',
      getProductByTierId: 'subscriptionStore/getProductByTierId',
      getCompanyId: 'main/getCompanyId',
      getAvailableTiers: 'subscriptionStore/getAvailableTiers',
      storedTier: 'subscriptionStore/getTier',
      paymentDetails: 'subscriptionStore/getPaymentDetails',
      isWaitingForResponse: 'main/isWaitingForResponse',
      billingSettings: 'subscriptionStore/getBillingSettings',
      nextPayment: 'subscriptionStore/getNextPayment',
      nextTier: 'subscriptionStore/getNextTier',
      currentTier: 'subscriptionStore/getCurrentTier',
    }),
    isUserPremium() {
      return this.activePlan != null
    },
    currentCycle() {
      return this.billingSettings.renewal_months
    },
    selectedCycleMultiplier() {
      return this.yearlyCycle ? 12 : 1
    },
    selectedCycleName() {
      return this.yearlyCycle ? this.$gettext('year') : this.$gettext('month')
    },
    selectedCycleNamely() {
      return this.yearlyCycle ? this.$gettext('yearly') : this.$gettext('monthly')
    },
    currentCycleNamely() {
      return this.currentCycle === 12 ? this.$gettext('yearly') : this.$gettext('monthly')
    },
    introText () {
      const intro_text =  this.$gettext('<h4>Pick your plan</h4>Can you not find a plan that matches your needs?<br>Contact us via <a href="%{ support_link }" rel="noopener" target="_blank" class="link-brand">customer support</a>.')
      const support_link = 'mailto:support@getgekko.com'
      return this.$gettextInterpolate(intro_text, { support_link: support_link })
    },
    discountText() {
      let discount_text = ''
      let discount_amount = this.activeDiscount.amount
      if (this.activeDiscount.benefit_type === 'amount') {
        discount_text = this.$gettext('%{ amount }€ OFF!')
      } else {
        discount_text = this.$gettext('%{ amount }% OFF!')
        discount_amount = discount_amount % 1 !== 0 ? discount_amount : Math.round(discount_amount)
      }
      return this.$gettextInterpolate(discount_text, { amount: discount_amount })
    },
    endBillingDate() {
      let end_date
      let dateformat = this.preferredLanguage === 'en' ? 'MM/DD/YYYY' : 'DD/MM/YYYY'
      if (this.yearlyCycle) {
        end_date = moment(Date.now()).add(1, 'years').format(dateformat)
      } else {
        end_date = moment(Date.now()).add(1, 'months').format(dateformat)
      }
      return end_date
    },
    updatedCycle() {
      let current_yearly = this.currentCycle === 12
      return this.yearlyCycle !== current_yearly
    },
    isActive() {
      return this.activePlan?.status === 'active' || this.activePlan?.status === 'grace'
    },
    isGrace() {
      return this.activePlan?.grace_date != null
    },
    isFree() {
      return this.activePlan == null || this.activePlan.planline_set?.some(planLine => planLine.is_free)
    },
    isRenewalDisabled() {
      return !this.billingSettings.auto_renewal
    },
    isCancelled() {
      return this.billingSettings.cancelled || false
    },
    beautifiedNextPayment() {
      if (this.nextPayment) {
        return this.nextPayment.toFixed(2)
      }
      return ''
    },
    rawFullPriceNext() {
      if (this.getProductByTierId(this.currentTier.id))
        return this.getProductByTierId(this.currentTier.id).cost * this.selectedCycleMultiplier
      else return 0
    },
    totalDiscountNext() {
      let discount
      if (this.activeDiscount.benefit_type === 'amount') {
        discount = this.activeDiscount.amount
      } else {
        let percentage = this.activeDiscount.amount * 0.01 // from % to a percentage
        discount = percentage * this.rawFullPriceNext
      }
      console.log('totalDiscountNext', discount, this.activeDiscount)
      return parseFloat(discount).toFixed(2)
    },
    nextPlanName() {
      return this.nextTier?.name
    },
  },
  methods: {
    ...mapMutations({
      setWaitingForResponse: 'main/SET_WAITING_FOR_RESPONSE',
    }),
    ...mapActions({
      loadSubscriptionsMainData: 'subscriptionStore/loadSubscriptionsMainData',
      storeUpdateSubscription: 'subscriptionStore/updateSubscription',
    }),
    setupData() {
      this.loading = true
      this.loadSubscriptionsMainData().then(() => {
        this.loading = false
      })
    },
    createPlan(tier_id) {
      PlanService.create({
        company_id: this.getCompanyId,
        months: this.selectedCycleMultiplier,
        tier: tier_id,
      }).then((response) => {
        var a_transaction_id = response.data
        const url = this.Urls['subscriptions:confirm_payment'](a_transaction_id)
        window.location.assign(url)
      })
    },
    updateSubscription(tier_id = null) { // TODO: remove this method if the update is removed
      this.setWaitingForResponse(true)
      this.storeUpdateSubscription({
        router: this.$router,
        payload: {
          yearlyCycle: this.yearlyCycle,
          updatedCycle: this.updatedCycle,
          tier: tier_id,
          updatedTier: this.currentTier.id !== tier_id,
          uncancelTier: this.currentTier.id === tier_id,
        },
      })
        .then(() => {
          this.loading = true
          this.loadSubscriptionsMainData().then(() => {
            if (this.updatedCycle) {
              this.$store.commit('setAlert', {
                title: this.$gettext('Subscription Updated'),
                text: this.$gettext('Updated billing Cycle.'),
                variant: 'success',
                duration: 4,
              })
            }
            this.loading = false
          })
        })
        .catch((err) => {
          console.error(err)
        })
        .finally(() => {
          this.setWaitingForResponse(false)
        })
    },
    repayPlan() {
      this.setWaitingForResponse(true)
      PlanService.repayPlan()
        .then((response) => {
          var a_transaction_id = response.data
          const url = this.Urls['subscriptions:confirm_payment'](a_transaction_id)
          window.location.assign(url)
        })
        .catch((response) => {
          if (response.status === 404) {
            this.$store.commit('setAlert', {
              title: this.$gettext('Error'),
              text: response.data.detail,
              variant: 'danger',
              duration: 4,
            })
          }
        })
        .finally(() => {
          this.setWaitingForResponse(false)
        })
    },
  },
}
</script>

<style lang="scss">
.accountantsubscriptions {
  .content {
    display: flex;
    justify-content: center;
    background: linear-gradient(165deg, #c0e5dc3d 0%, white 50%) !important;
    padding: 1rem 3rem !important;
  }
  .subscriptions-wrap {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    // max-width: 1200px;
    padding: 1.5rem 0;
    .plan-cycle-wrap {
      padding-top: 1.5rem;
      padding-bottom: 1rem;
      .billingcycle-date {
        text-align: center;
        display: block;
        font-size: 14px;
        color: #b9b4b4;
        margin-bottom: 0.5rem;
      }
    }
    &--activeplan {
      align-items: flex-start;
      font-size: 1rem;
      .subscriptions-activeplan {
        display: flex;
        align-items: flex-start;
        gap: 2rem;
        width: 100%;
        max-width: 1200px;
        @media (max-width: 976px) {
          flex-direction: column;
        }
        .activeplan-wrap {
          padding-top: 2rem;
        }
      }
      .next-payment-alert {
        display: inline-block;
        text-align: center;
      }
    }
  }
  .subscriptions-tiers-wrap {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 1rem;
    width: 100%;
    max-width: 1490px;
  }

  /* tablet when submenu not collapsed */
  @media (max-width: 1500px) {
    .accountants-submenu:not(.collapsed) ~ .main-row .subscriptions-tiers-wrap {
      max-width: 740px;
      grid-template-columns: repeat(2, 1fr);
    }
  }

  /* mobile when submenu not collapsed */
  @media (max-width: 900px) {
    .accountants-submenu:not(.collapsed) ~ .main-row .subscriptions-tiers-wrap {
      max-width: 360px;
      grid-template-columns: 1fr;
    }
  }

  /* tablet when submenu collapsed */
  @media (max-width: 1300px) {
    .accountants-submenu.collapsed ~ .main-row .subscriptions-tiers-wrap {
      max-width: 740px;
      grid-template-columns: repeat(2, 1fr);
    }
  }

  /* mobile when submenu collapsed */
  @media (max-width: 640px) {
    .accountants-submenu.collapsed ~ .main-row .subscriptions-tiers-wrap {
      max-width: 360px;
      grid-template-columns: 1fr;
    }
    .content {
      padding: 1rem !important;
    }
  }

  .plan-cycle-switch-wrap {
    .left-selector,
    .right-selector {
      font-size: 18px;
    }
  }
}
</style>
